import React, { createContext, useContext } from 'react';
import { GoalFormApi } from './types';

// GoalFormApi<T> is invariant in T (due to function params that both accept and return T).
// React context requires a single concrete type, but providers pass different T
// (ObjectiveFormData, KeyResultFormData, etc.). We erase the type parameter here;
// consumers recover the correct T via useGoalFormContext<T>().
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- variance erasure for generic React context
const GoalFormContext = createContext<GoalFormApi<any> | null>(null);

type Props = {
  // GoalFormApi<T> is invariant in T, so GoalFormApi<SomeConcreteType> cannot be
  // assigned to GoalFormApi<any> due to strictFunctionTypes. We must fully erase the
  // form type. Consumers recover the correct type via useGoalFormContext<T>().
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- required: invariant generic cannot be widened without full type erasure
  form: any;
  children: React.ReactNode;
};

export const GoalFormProvider: React.FC<Props> = ({ form, children }) => {
  return (
    <GoalFormContext.Provider value={form}>{children}</GoalFormContext.Provider>
  );
};

export function useGoalFormContext<
  T extends Record<string, unknown>,
>(): GoalFormApi<T> {
  const ctx = useContext(GoalFormContext);
  if (!ctx) {
    throw new Error(
      'useGoalFormContext must be used within a GoalFormProvider',
    );
  }
  return ctx as GoalFormApi<T>;
}
